<script setup lang="ts">
import { ref, useTemplateRef } from 'vue';

import { type NoticeType } from '@/domains/notifications/notice-icons';
import CDAlert from '@/shared/ui/CDAlert.vue';

import type { FormattedNotifyMsg } from '@/domains/notifications/queries';

// Welcome notices get a multi-line title-over-content layout instead of the
// flashbar's title-then-1-line-desc layout.
const WELCOME_TITLE = 'Welcome to PhoenixAI';

const {
  item,
  pending,
  compact = false,
} = defineProps<{
  item: FormattedNotifyMsg;
  pending: boolean;
  // compact = single-line ellipsis on desc (uniform card height for the
  // collapsed stack deck); !compact = up to 4 lines + ellipsis (used in
  // the expanded list where there's room for more content).
  compact?: boolean;
}>();

const emit = defineEmits<{
  'mark-read': [item: FormattedNotifyMsg];
  jump: [item: FormattedNotifyMsg];
}>();

// Measure truncation lazily on pointer enter. The element's layout is
// already stable at that moment, so the read is accurate without watching
// resize / font-load / animation-end. Compact mode overflows horizontally
// (white-space: nowrap); expanded mode overflows vertically
// (-webkit-line-clamp). Checking both dimensions covers either case.
const descEl = useTemplateRef<HTMLElement>('descEl');
const descTruncated = ref(false);

function checkDescTruncation(): void {
  const el = descEl.value;
  if (!el) {
    descTruncated.value = false;
    return;
  }
  descTruncated.value =
    el.scrollWidth - el.clientWidth > 1 || el.scrollHeight - el.clientHeight > 1;
}

const isWelcome = (): boolean => item.title === WELCOME_TITLE;

function onClose(): void {
  if (item.detail_biz_id) emit('mark-read', item);
}
</script>

<template>
  <div
    :class="{ 'cursor-pointer': item.jumpDetail }"
    data-testid="notification-card-body"
    :data-welcome="isWelcome() ? 'true' : 'false'"
    @click="emit('jump', item)"
  >
    <CDAlert
      :type="item.msg_type as NoticeType"
      :title="item.title"
      :pending="pending"
      show-icon
      @close="onClose"
    >
      <ElTooltip
        :disabled="!descTruncated"
        placement="top-end"
        :show-after="200"
        :show-arrow="false"
        popper-class="!bg-surface-elevated !text-fg"
      >
        <template #content>
          <div class="max-w-[380px] leading-normal break-words whitespace-pre-line">
            {{ item.content }}
          </div>
        </template>
        <div
          ref="descEl"
          :class="[compact ? 'truncate' : 'line-clamp-4 break-words whitespace-pre-line']"
          @pointerenter="checkDescTruncation"
        >
          {{ item.content }}
        </div>
      </ElTooltip>
    </CDAlert>
  </div>
</template>
